import { useCallback, useEffect, useRef, useState } from 'react';

const FX_ENDPOINT = 'https://open.er-api.com/v6/latest/USD';

export interface UsdZarRate {
  rate: number | null;
  fetchedAt: string | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

interface ErApiResponse {
  result?: string;
  rates?: Record<string, number>;
  time_last_update_utc?: string;
  'error-type'?: string;
}

export function useUsdZarRate(): UsdZarRate {
  const [rate, setRate] = useState<number | null>(null);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState<number>(0);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    setLoading(true);
    setError(null);

    fetch(FX_ENDPOINT, { headers: { Accept: 'application/json' } })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`FX request failed (HTTP ${response.status})`);
        }
        const body = (await response.json()) as ErApiResponse;
        if (body.result && body.result !== 'success') {
          throw new Error(`FX request failed (${body['error-type'] || body.result})`);
        }
        const zar = body.rates?.['ZAR'];
        if (typeof zar !== 'number' || !Number.isFinite(zar) || zar <= 0) {
          throw new Error('FX response missing ZAR rate');
        }
        if (cancelledRef.current) return;
        setRate(zar);
        setFetchedAt(body.time_last_update_utc || new Date().toISOString());
      })
      .catch((err: unknown) => {
        if (cancelledRef.current) return;
        setRate(null);
        setFetchedAt(null);
        setError(err instanceof Error ? err.message : 'Failed to fetch USD→ZAR rate');
      })
      .finally(() => {
        if (!cancelledRef.current) setLoading(false);
      });

    return () => {
      cancelledRef.current = true;
    };
  }, [tick]);

  const refetch = useCallback(() => {
    setTick((n) => n + 1);
  }, []);

  return { rate, fetchedAt, loading, error, refetch };
}
